import { useEffect, useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { api, formatPrice } from '@/lib/api';
import { Crown, Mail, User, Package } from 'lucide-react';

export default function Account() {
    const { user, loading } = useAuth();
    const [orders, setOrders] = useState([]);

    useEffect(() => {
        if (user) api.get('/orders/my').then(({ data }) => setOrders(data.items || [])).catch(() => {});
    }, [user]);

    if (loading) return <div className="max-w-7xl mx-auto p-8 text-white/50">Loading…</div>;
    if (!user) return <Navigate to="/login" replace />;

    return (
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
            <h1 className="font-display text-4xl text-white mb-6">MY ACCOUNT</h1>
            <div className="grid md:grid-cols-3 gap-4">
                <div className="rrm-panel p-5">
                    <User className="w-5 h-5 text-amber-400" />
                    <div className="mt-2 text-xs text-white/50 tracking-widest">USERNAME</div>
                    <div className="text-white">{user.username}</div>
                </div>
                <div className="rrm-panel p-5">
                    <Mail className="w-5 h-5 text-amber-400" />
                    <div className="mt-2 text-xs text-white/50 tracking-widest">EMAIL</div>
                    <div className="text-white break-all">{user.email}</div>
                </div>
                <div className="rrm-panel p-5">
                    <Crown className="w-5 h-5 text-amber-400" />
                    <div className="mt-2 text-xs text-white/50 tracking-widest">MEMBERSHIP</div>
                    <div className="text-white">{user.is_member ? 'Active member' : 'Not a member'}</div>
                    {!user.is_member && <Link to="/join" className="text-xs text-amber-300">Activate</Link>}
                </div>
            </div>

            <div className="mt-8">
                <h2 className="font-display text-2xl text-white mb-3 inline-flex items-center gap-2"><Package className="w-5 h-5 text-amber-400" /> ORDERS</h2>
                {orders.length === 0 ? (
                    <div className="rrm-panel p-6 text-white/60">No orders yet. <Link className="text-amber-300" to="/shop">Start shopping</Link></div>
                ) : (
                    <div className="rrm-panel divide-y divide-white/5">
                        {orders.map((o) => (
                            <div key={o.id} className="p-4">
                                <div className="flex items-center justify-between">
                                    <div className="font-mono text-xs text-white/50">{o.session_id}</div>
                                    <div className="text-amber-400 font-display text-xl">{formatPrice(o.amount)}</div>
                                </div>
                                <div className="text-xs text-white/50 mt-1">{new Date(o.created_at).toLocaleString()} · {o.status}</div>
                                <div className="mt-3 grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                                    {o.items?.map((it, i) => (
                                        <div key={i} className="flex items-center gap-2 text-sm">
                                            <img src={it.image_url} alt={it.name} className="w-10 h-10 rounded object-cover bg-black/40" />
                                            <div className="min-w-0">
                                                <div className="line-clamp-1 text-white">{it.name}</div>
                                                <div className="text-xs text-white/50">x{it.quantity} · {formatPrice(it.price)}</div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
